import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, FileText, RefreshCw } from 'lucide-react'
import Button from '../components/ui/Button'
import Loading from '../components/ui/Loading'
import TranscriptionCard from '../components/transcription/TranscriptionCard'
import { useUserTranscriptions } from '../hooks/useTranscription'
import { transcriptionService } from '../services/transcriptionService'

const DashboardPage = () => {
  const navigate = useNavigate()
  const { transcriptions, loading, error, refetch } = useUserTranscriptions()
  const [deletingId, setDeletingId] = useState(null)

  const handleNewUpload = () => {
    navigate('/upload')
  }

  const handleViewTranscription = (transcription) => {
    navigate(`/transcription/${transcription.id}`)
  }

  const handleDownloadTranscription = (transcription) => {
    transcriptionService.downloadTranscription(transcription)
  }

  const handleDeleteTranscription = async (transcription) => {
    if (!window.confirm(`确定要删除 "${transcription.original_filename}" 吗？此操作无法撤销。`)) {
      return
    }

    try {
      setDeletingId(transcription.id)
      await transcriptionService.deleteTranscription(
        transcription.id, 
        transcription.audio_file_path
      )
      await refetch() // 刷新列表
    } catch (error) {
      alert('删除失败：' + error.message)
    } finally {
      setDeletingId(null)
    }
  }

  const completedCount = transcriptions.filter(t => t.status === 'completed').length
  const processingCount = transcriptions.filter(t => t.status === 'processing').length
  const failedCount = transcriptions.filter(t => t.status === 'failed').length

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 页面头部 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              我的转录记录
            </h1>
            <p className="text-gray-600 mt-2">
              管理您的音频转录文件和结果
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={refetch}
              loading={loading}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              刷新
            </Button>
            
            <Button onClick={handleNewUpload}>
              <Plus className="h-4 w-4 mr-2" />
              新建转录
            </Button>
          </div>
        </div>

        {/* 统计信息 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">
              {transcriptions.length}
            </div>
            <div className="text-gray-600">总转录数</div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-green-600">
              {completedCount}
            </div>
            <div className="text-gray-600">已完成</div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-yellow-600">
              {processingCount}
            </div>
            <div className="text-gray-600">处理中</div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-red-600">
              {failedCount}
            </div>
            <div className="text-gray-600">处理失败</div>
          </div>
        </div>

        {/* 转录记录列表 */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loading size="lg" text="加载转录记录..." />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600">{error}</p>
            <Button
              variant="outline"
              onClick={refetch}
              className="mt-4"
            >
              重试
            </Button>
          </div>
        ) : transcriptions.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              还没有转录记录
            </h3>
            <p className="text-gray-600 mb-6">
              上传您的第一个音频文件开始使用转录服务
            </p>
            <Button onClick={handleNewUpload}>
              <Plus className="h-4 w-4 mr-2" />
              上传音频文件
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {transcriptions.map((transcription) => (
              <TranscriptionCard
                key={transcription.id}
                transcription={transcription}
                onView={handleViewTranscription}
                onDownload={handleDownloadTranscription}
                onDelete={handleDeleteTranscription}
                className={deletingId === transcription.id ? 'opacity-50' : ''}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardPage